import { Location } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
    Signal,
    ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { combineLatest, Observable, Subscription } from "rxjs";
import {
    distinctUntilChanged,
    filter,
    map,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import { NotificationsService } from "@hermes/aphrodite/layout";
import { Context, INFRA_SETTINGS, LOCALE } from "@hermes/app-core";
import { InfraSettings } from "@hermes/env-infra";
import { SeoLink } from "@hermes/fragments/seo-links";
import { Locale } from "@hermes/locale";
import { FilterHatchService } from "@hermes/pages/grid-page";
import {
    FeatureFlagFacade,
    GRID_FILTER_REVAMP,
    SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
} from "@hermes/states/flipper";
import {
    Facet,
    getPageSizeValue,
    GridAlternatives,
    GridCategory,
    GridFacade,
    GridFilter,
    GridFilterGroup,
    GridType,
    OTHER_PAGE,
} from "@hermes/states/grid-filters-state";
import { MenuFacade } from "@hermes/states/menu";
import { getFacetQueryParameter } from "@hermes/trays/grid-filters";

/**
 * Principal container for the product grid
 */
@Component({
    selector: "h-grid-page",
    templateUrl: "./grid-page.component.html",
    styleUrls: ["./grid-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridPageComponent implements OnInit, OnDestroy {
    @ViewChild("gridFilterBarContainer")
    public gridFilterBar: ElementRef;

    public displayNoResult = false;
    public isSuggestionGrid = false;
    public facetSEOLinks: SeoLink[] = [];

    public pagesRoutes: string[] = [];

    public missingFacet$: Observable<boolean>;

    public resultGridHeader$: Observable<{
        collectionSize: number;
        facetDisplayName: string;
        currentCategory: GridCategory;
        hideTotalUpdatedItems: boolean;
    }> = this.gridFacade.resultGridHeader$;

    public readonly isGridHighlightsEnabledSignal: Signal<boolean>;

    public isSearchGrid$: Observable<boolean> = this.gridFacade.isSearchGrid$;

    public isGridFilterRevampEnabled$: Observable<boolean> =
        this.featureFlagFacade.isActivated(GRID_FILTER_REVAMP);

    /**
     * SEO Links must NOT be visible when :
     * - Hero product grid page displayed
     * - Is not a finalCategory page (search page excepted)
     * - Is a faceted page
     * - IS a search grid
     */
    public isGridSeoPaginationEnabled$: Observable<boolean> = combineLatest([
        this.gridFacade.category$.pipe(
            switchMap((category) =>
                this.menuFacade.isFinalCategory(category?.pimCode ?? ""),
            ),
        ),
        this.gridFacade.gridType$,
    ]).pipe(
        map(
            ([isFinalCategory, gridType]) =>
                isFinalCategory && gridType === GridType.category,
        ),
    );

    // Get pageType for Livechat
    public pageType$ = this.gridFacade.pageType$;

    private subscription: Subscription = new Subscription();

    constructor(
        private context: Context,
        private notificationsService: NotificationsService,
        private menuFacade: MenuFacade,
        private gridFacade: GridFacade,
        @Inject(INFRA_SETTINGS) private infraSettings: InfraSettings,
        @Inject(LOCALE) private locale: Locale,
        private location: Location,
        private router: Router,
        private filterHatchService: FilterHatchService,
        public cd: ChangeDetectorRef,
        private featureFlagFacade: FeatureFlagFacade,
    ) {
        this.isGridHighlightsEnabledSignal = toSignal(
            this.gridFacade.isGridHighlighsEnabled$,
            {
                initialValue: false,
            },
        );

        if (this.context.isInBrowserMode()) {
            this.subscription.add(
                // Only in the case of initialization
                // Angular routing rewrite the url, which conflicts with our writing the url in fetchGridDatasSuccess of gridEffect
                // So we need to put the real url back after the end of navigation
                this.router.events
                    .pipe(filter((event) => event instanceof NavigationEnd))
                    .subscribe(() => this.gridFacade.updateUrl()),
            );
            // When navigate out of grid page scope, we need to update previous page value in State for the metadata to be
            // re-apply when we go back to category page
            this.subscription.add(
                this.router.events
                    .pipe(
                        filter(
                            (event) =>
                                event instanceof NavigationStart &&
                                !event.url.startsWith("/product/") &&
                                !event.url.startsWith("/category/"),
                        ),
                    )
                    .subscribe(() => {
                        this.gridFacade.changePreviousPage(OTHER_PAGE);
                    }),
            );
        }
    }
    /**
     * Handles click events for the entire document. Using 'document:click' allows the listener
     * to detect clicks anywhere on the page.
     * This differs from using just 'click', which would only detect clicks
     * only inside the component or its children.
     */
    @HostListener("document:click", ["$event"])
    public onClickCloseToggles(event: Event): void {
        if (!this.gridFilterBar?.nativeElement?.contains(event.target)) {
            // Close all toggles if clicked outside h-grid-filters-bar
            this.filterHatchService.clearGridFilter();
        }
    }

    /**
     * On Init, we get the FH response and we parse it
     */
    public ngOnInit(): void {
        this.missingFacet$ = this.gridFacade.missingFacet$;

        // Subscription on alternative service on a search case
        this.subscription.add(
            this.gridFacade.alternatives$.subscribe(
                (response: GridAlternatives) => {
                    if (response) {
                        this.isSuggestionGrid = response.total === 0;
                        this.cd.markForCheck();
                    }
                },
            ),
        );

        this.subscription.add(
            combineLatest([
                this.gridFacade.facet$,
                this.gridFacade.filterGroups$,
                this.featureFlagFacade.isActivated(
                    SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
                ),
            ]).subscribe(([facet, filterGroups, isCombinedFacetEnabled]) => {
                this.facetSEOLinks = this.generateFacetSEOLinks(
                    filterGroups,
                    isCombinedFacetEnabled,
                    facet,
                );
            }),
        );

        // Subscription to the products service to get the total of products
        this.subscription.add(
            this.gridFacade.products$
                .pipe(
                    filter((products) => !!products),
                    withLatestFrom(this.gridFacade.pendingResultItems$),
                    map(
                        ([products, pending]) =>
                            products.length === 0 && !pending,
                    ),
                )
                .subscribe((displayNoResult: boolean) => {
                    // if cde response without product we have to display no result grid
                    this.displayNoResult = displayNoResult;
                    this.cd.markForCheck();
                }),
        );

        this.subscription.add(
            this.gridFacade.total$
                .pipe(
                    filter(Boolean),
                    distinctUntilChanged(),
                    withLatestFrom(
                        this.gridFacade.pageNumber$,
                        this.isGridSeoPaginationEnabled$,
                    ),
                    tap(([total, pageNumber, isSeoPaginationEnabled]) => {
                        this.gridFacade.updateSeoPaginationHeadLinks(
                            isSeoPaginationEnabled,
                            total,
                            pageNumber,
                        );
                    }),
                    map(([total]) => {
                        // Get page size according to grid type (Hero product or not)
                        const viewSize = getPageSizeValue().viewSize;

                        // Get an array containing all pages needed to present the total number of products
                        return [
                            ...Array.from({
                                length: Math.ceil(total / viewSize),
                            }).keys(),
                        ];
                    }),
                    map((pageNumbers: number[]) =>
                        // Conversion of each page number to its page url
                        pageNumbers.map((pageNumber) => {
                            if (pageNumber === 0) {
                                return this.getCurrentUrl();
                            }
                            return `${this.getCurrentUrl()}?page=${
                                pageNumber + 1
                            }`;
                        }),
                    ),
                )
                .subscribe((pagesRoutes: string[]) => {
                    this.pagesRoutes = pagesRoutes;
                    this.cd.markForCheck();
                }),
        );

        this.subscription.add(
            this.gridFacade.pendingResultItems$.subscribe(
                (pending: boolean) => {
                    if (pending) {
                        this.notificationsService.showLoader();
                        return;
                    }
                    this.notificationsService.hideLoader();
                },
            ),
        );
    }

    public ngOnDestroy(): void {
        this.gridFacade.clearMenuLevel();
        this.subscription.unsubscribe();
    }

    public closeBannerFacet(): void {
        this.gridFacade.updateMissingFacet(false);
    }

    private generateFacetSEOLinks(
        filterGroups: GridFilterGroup[],
        isCombinedFacetEnabled: boolean,
        appliedFacet: Facet,
    ): SeoLink[] {
        return filterGroups
            .filter((filterGroup) => this.isValidFacetGroup(filterGroup))
            .flatMap((filterGroup) =>
                this.getCrawlableFilters(filterGroup).map((fhFilter) => {
                    const currentAppliedFacet: Record<string, string> = {};

                    for (const current of appliedFacet.combinedFacets) {
                        currentAppliedFacet[current.facetName] =
                            current.facetNameValue;
                    }

                    const facetQueryParameters = getFacetQueryParameter(
                        fhFilter,
                        filterGroup.facetUrlParameterName,
                        currentAppliedFacet,
                        isCombinedFacetEnabled,
                    );
                    return {
                        path: `${this.getCurrentUrl()}?${facetQueryParameters}`,
                        label: `${fhFilter.name} (${fhFilter.total})`,
                    };
                }),
            );
    }

    private isValidFacetGroup(filterGroup: GridFilterGroup): boolean {
        return !!filterGroup.facetUrlParameterName?.trim();
    }

    private getCrawlableFilters(filterGroup: GridFilterGroup): GridFilter[] {
        return filterGroup.filters.filter(
            (fhFilter) => fhFilter.facetIsCrawlable,
        );
    }

    private getCurrentUrl(): string {
        const currentURL = `${this.infraSettings.frontend}${
            this.locale.urlPrefix
        }${this.location.path().split("?")[0]}`;
        return decodeURI(currentURL);
    }
}
